import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, KeyRound, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { authService } from '../services/authService';
import { getErrorMessage } from '../utils/errorMessage';

/**
 * Pagina del link di recupero (`/reimposta-password?token=…`): stesse regole
 * del cambio password (almeno 8 caratteri, diversa dalla precedente). Dopo il
 * cambio tutte le sessioni sono chiuse: si rientra con la nuova password.
 */
export default function ReimpostaPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const valid = password.length >= 8 && password === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || !token) return;
    setError(null);
    setIsLoading(true);
    try {
      await authService.resetPassword(token, password);
      setDone(true);
    } catch (err: any) {
      setError(err?.response
        ? getErrorMessage(err, 'Impossibile reimpostare la password.')
        : 'Errore di connessione. Riprova tra qualche istante.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = 'w-full h-10 pl-9 pr-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]';

  return (
    <div data-testid="reset-password-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-lg mx-auto space-y-4">
          <div className="rounded-xl border bg-white p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[hsl(210,40%,93%)] flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-[hsl(210,64%,29%)]" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">Nuova password</h1>
                <p className="text-sm text-muted-foreground">Scegli la password per il tuo accesso</p>
              </div>
            </div>

            {!token ? (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="reset-password-no-token">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  Il link non è completo. Aprilo di nuovo dall'email, oppure{' '}
                  <Link to="/password-dimenticata" className="font-medium underline">chiedine uno nuovo</Link>.
                </div>
              </div>
            ) : done ? (
              <div className="space-y-4" data-testid="reset-password-done">
                <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Password cambiata. Per sicurezza abbiamo chiuso tutti gli accessi aperti: entra con la nuova password.
                </div>
                <Link
                  to="/accedi"
                  className="w-full h-10 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[hsl(210,64%,24%)] transition"
                  data-testid="reset-password-go-login"
                >
                  Accedi <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <>
                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 mb-4 text-sm text-red-700" data-testid="reset-password-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      {error}{' '}
                      <Link to="/password-dimenticata" className="font-medium underline">Chiedi un nuovo link</Link>
                    </div>
                  </div>
                )}
                <form className="space-y-4" onSubmit={handleSubmit} data-testid="reset-password-form">
                  <p className="text-xs text-muted-foreground">
                    La nuova password deve essere lunga almeno 8 caratteri e diversa dalla precedente.
                  </p>
                  <div>
                    <label className="text-xs font-medium mb-1.5 block">Nuova password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="password"
                        className={inputClass}
                        placeholder="Almeno 8 caratteri"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                        minLength={8}
                        maxLength={200}
                        data-testid="reset-password-new"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1.5 block">Conferma password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="password"
                        className={inputClass}
                        placeholder="Ripeti la nuova password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        autoComplete="new-password"
                        maxLength={200}
                        data-testid="reset-password-confirm"
                        required
                      />
                    </div>
                    {mismatch && <p className="text-xs text-red-600 mt-1">Le password non coincidono</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading || !valid}
                    data-testid="reset-password-submit"
                    className="w-full h-10 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[hsl(210,64%,24%)] transition disabled:opacity-60"
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    ) : (
                      <>Salva la nuova password <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
